import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
    BigModal,
    TextInput,
    ThemedButton,
    TextLead,
} from '@awarego-ui/components';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ThemedSpinner from '../../../components/themed/ThemedSpinner';

const EditUserTagsModal = ({
    isOpen,
    onRequestClose,
    user,
    saving,
    onSave,
}) => {
    const initialTags = useMemo(() => {
        if (!user || !Array.isArray(user.tags)) return [];
        return user.tags;
    }, [user]);

    const [tags, setTags] = useState(initialTags);

    useEffect(() => {
        setTags(initialTags);
    }, [initialTags, isOpen]);

    const updateTagAt = (index, value) => {
        setTags((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    const addTag = () => {
        setTags((prev) => [...prev, '']);
    };

    const removeTag = (index) => {
        setTags((prev) => prev.filter((_, i) => i !== index));
    };

    const normalizedTags = useMemo(() => {
        return Array.from(
            new Set(
                tags
                    .filter((tag) => typeof tag === 'string')
                    .map((tag) => tag.trim())
                    .filter((tag) => tag.length > 0)
            )
        );
    }, [tags]);

    const tagsChanged =
        JSON.stringify(normalizedTags) !== JSON.stringify(initialTags);

    const handleSave = async () => {
        if (!onSave) return;
        await onSave(normalizedTags);
    };

    return (
        <BigModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            heading={
                user
                    ? `Edit tags for ${user.name || user.email}`
                    : 'Edit tags'
            }
            extraHeaderContent={
                <TextLead lighter mt={4}>
                    Tags help you organize users — add or remove them as needed.
                </TextLead>
            }
            modalContent={
                <Box display="flex" flexDirection="column" gap={0}>
                    {tags.length === 0 && (
                        <Box
                            sx={{ color: 'text.secondary', fontSize: 14 }}
                        >
                            No tags assigned. Use the button below to add one.
                        </Box>
                    )}
                    {tags.map((tag, index) => (
                        <Box
                            key={`${index}-${user?.email ?? 'user'}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <TextInput
                                stretched
                                heightSmall
                                inline
                                placeholder="Enter a tag"
                                value={tag}
                                onChange={(event) =>
                                    updateTagAt(index, event.target.value)
                                }
                            />
                            <IconButton
                                aria-label="Delete tag"
                                onClick={() => removeTag(index)}
                                size="large"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Box>
                        <ThemedButton
                            mode="link"
                            text="+ Add tag"
                            onClick={addTag}
                        />
                    </Box>
                </Box>
            }
            modalFooterContent={
                saving ? (
                    <ThemedSpinner size={40} />
                ) : (
                    <>
                        <ThemedButton
                            mode="secondary"
                            text="Cancel"
                            onClick={onRequestClose}
                        />
                        <ThemedButton
                            mode="primary"
                            text="Save"
                            onClick={handleSave}
                            disabled={!tagsChanged}
                        />
                    </>
                )
            }
        />
    );
};

export default observer(EditUserTagsModal);

